import axios, { AxiosResponse } from "axios";
import { getApiBaseUrl } from "@/config/apiBaseUrl";

const API_BASE_URL = getApiBaseUrl();

export interface LoginRequest {
  email: string;
  password: string;
  totp_code: string;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  user?: {
    id: string;
    email: string;
    role: "ADMIN" | "MANAGER" | "ACCOUNTANT" | "CLIENT";
    organization_id?: number | null;
    is_org_admin?: boolean;
    user_name?: string | null;
    creation_dt?: string;
    last_activity_dt?: string;
    must_change_password?: boolean;
  };
  requires_totp?: boolean;
  requires_password_change?: boolean;
  error?: string;
  message?: string;
}

export interface LoginResult {
  success: boolean;
  requiresTotp: boolean;
  requiresPasswordChange: boolean;
  error?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  role: "ADMIN" | "MANAGER" | "ACCOUNTANT" | "CLIENT";
  organization_id?: number | null;
  is_org_admin?: boolean;
  user_name?: string | null;
  creation_dt?: string;
  last_activity_dt?: string;
  must_change_password?: boolean;
}

export interface RegisterOrganizationRequest {
  inn: string;
  full_name: string;
  short_name?: string;
  ogrn?: string;
  kpp?: string;
  address?: string;
  admin_email: string;
  admin_password: string;
}

export interface RegisterOrganizationResponse {
  success: boolean;
  organization_id?: number;
  user_id?: number;
  message?: string;
  error?: string;
  error_code?: string;
}

export interface CheckInnResponse {
  available: boolean;
  message?: string;
}

export interface CheckEmailResponse {
  available: boolean;
  message?: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface RequestPasswordResetResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/** Ответ POST /v2/auth/refresh. */
export interface RefreshTokenResponse {
  success: boolean;
  access_token?: string;
  expires_in?: number;
  error?: string;
}

/**
 * За сколько до истечения токена начинаем продление.
 *
 * Пять минут — компромисс: достаточно, чтобы пережить сетевые сбои и успеть
 * повторить попытку, и при этом токен не обновляется чаще необходимого.
 */
const REFRESH_LEAD_MS = 5 * 60 * 1000;

/** Пауза перед повторной попыткой, если продление сорвалось (например, оборвалась сеть). */
const REFRESH_RETRY_MS = 60 * 1000;

/**
 * Декодирует base64url-сегмент JWT.
 *
 * Своя реализация вместо библиотеки: нужен ровно один сегмент payload,
 * подпись мы не проверяем — это делает бэкенд.
 */
function decodeBase64Url(segment: string): string {
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padding = (4 - (normalized.length % 4)) % 4;
  const binary = atob(normalized + "=".repeat(padding));
  // atob возвращает байты, а payload — UTF-8, поэтому пересобираем строку вручную
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Возвращает момент истечения токена в миллисекундах epoch или null,
 * если это не JWT либо в payload нет `exp`.
 *
 * null означает «срок неизвестен» — такой токен не считаем протухшим,
 * иначе нестандартный токен молча заблокировал бы вход.
 */
export function readTokenExpiry(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) {
    return null;
  }
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1])) as { exp?: unknown };
    if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) {
      return null;
    }
    return payload.exp * 1000;
  } catch (error) {
    console.error("Не удалось разобрать JWT:", error);
    return null;
  }
}

/**
 * Приводит значение параметра `redirect` к безопасному внутреннему пути.
 *
 * Пускаем только пути вида `/что-то`: «//host» и «/\host» браузер трактует
 * как переход на чужой домен, то есть это открытый редирект.
 */
export function sanitizeRedirectTarget(rawTarget: string | null | undefined): string {
  if (!rawTarget) {
    return "/";
  }
  if (!rawTarget.startsWith("/") || rawTarget.startsWith("//") || rawTarget.startsWith("/\\")) {
    return "/";
  }
  // Возврат на саму форму входа зациклил бы вход
  if (rawTarget === "/login" || rawTarget.startsWith("/login?")) {
    return "/";
  }
  return rawTarget;
}

/** Собирает адрес формы входа, запоминая страницу, с которой пользователя увели. */
export function buildLoginUrl(target: string): string {
  const safeTarget = sanitizeRedirectTarget(target);
  return safeTarget === "/" ? "/login" : `/login?redirect=${encodeURIComponent(safeTarget)}`;
}

class AuthService {
  private token: string | null = null;
  private user: AuthUser | null = null;
  /** Момент истечения токена (ms epoch); null — срок неизвестен. */
  private tokenExpiresAt: number | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** Общий промис активного продления — чтобы параллельные 401 не слали пачку refresh. */
  private refreshInFlight: Promise<boolean> | null = null;
  private focusHandler: (() => void) | null = null;
  private sessionExpiredHandler: (() => void) | null = null;

  constructor() {
    // Восстанавливаем данные аутентификации из localStorage при инициализации
    this.restoreAuthState();
  }

  /**
   * Аутентификация пользователя
   */
  async login(email: string, password: string, totpCode: string): Promise<LoginResult> {
    try {
      const response: AxiosResponse<LoginResponse> = await axios.post(`${API_BASE_URL}/v2/auth/login`, {
        email,
        password,
        totp_code: totpCode,
      });

      const {
        success,
        token,
        user,
        message,
        error,
        requires_totp: requiresTotp = false,
        requires_password_change: requiresPasswordChange = false,
      } = response.data;

      if (success && token && user) {
        this.applyToken(token);
        this.user = user;
        this.saveAuthState();
        return {
          success: true,
          requiresTotp,
          requiresPasswordChange,
        };
      }

      const errorMessage = error || message || "Ошибка входа";
      console.error("Login failed:", errorMessage);
      return {
        success: false,
        requiresTotp,
        requiresPasswordChange,
        error: errorMessage,
      };
    } catch (error) {
      console.error("Login error:", error);
      if (axios.isAxiosError(error)) {
        const detail = (error.response?.data as { detail?: string; error?: string; message?: string } | undefined)
          ?.detail;
        const apiError =
          detail ||
          (error.response?.data as { error?: string; message?: string } | undefined)?.error ||
          (error.response?.data as { error?: string; message?: string } | undefined)?.message;
        return {
          success: false,
          requiresTotp: false,
          requiresPasswordChange: false,
          error: apiError || "Ошибка соединения с сервером",
        };
      }
      return {
        success: false,
        requiresTotp: false,
        requiresPasswordChange: false,
        error: "Ошибка соединения с сервером",
      };
    }
  }

  /**
   * Смена пароля текущего пользователя
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<ChangePasswordResponse> {
    try {
      const response: AxiosResponse<ChangePasswordResponse> = await axios.post(
        `${API_BASE_URL}/v2/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
        {
          headers: {
            ...this.getAuthHeaders(),
          },
        },
      );

      if (this.user) {
        this.user = {
          ...this.user,
          must_change_password: false,
        };
        this.saveAuthState();
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
        return {
          success: false,
          error: detail || "Не удалось изменить пароль",
        };
      }
      return {
        success: false,
        error: "Не удалось изменить пароль",
      };
    }
  }

  /**
   * Запрос ссылки для восстановления пароля.
   *
   * Бэкенд всегда возвращает 200, чтобы не раскрывать существование email.
   */
  async requestPasswordReset(email: string): Promise<RequestPasswordResetResponse> {
    try {
      const response: AxiosResponse<RequestPasswordResetResponse> = await axios.post(
        `${API_BASE_URL}/v2/auth/request-password-reset`,
        { email },
      );
      return response.data;
    } catch (error) {
      console.error("Request password reset error:", error);
      if (axios.isAxiosError(error)) {
        const detail = (error.response?.data as { detail?: string; error?: string; message?: string } | undefined)
          ?.detail;
        const apiError =
          detail ||
          (error.response?.data as { error?: string; message?: string } | undefined)?.error ||
          (error.response?.data as { error?: string; message?: string } | undefined)?.message;
        return {
          success: false,
          error: apiError || "Ошибка соединения с сервером",
        };
      }
      return {
        success: false,
        error: "Ошибка соединения с сервером",
      };
    }
  }

  /**
   * Регистрация организации
   */
  async register(data: RegisterOrganizationRequest): Promise<RegisterOrganizationResponse> {
    try {
      const response: AxiosResponse<RegisterOrganizationResponse> = await axios.post(
        `${API_BASE_URL}/v2/auth/register`,
        data,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return error.response.data as RegisterOrganizationResponse;
      }
      return {
        success: false,
        error: "Ошибка соединения с сервером",
        error_code: "CONNECTION_ERROR",
      };
    }
  }

  /**
   * Проверка доступности ИНН
   */
  async checkInn(inn: string): Promise<CheckInnResponse> {
    try {
      const response: AxiosResponse<CheckInnResponse> = await axios.get(
        `${API_BASE_URL}/v2/auth/check-inn/${encodeURIComponent(inn)}`,
      );
      return response.data;
    } catch (error) {
      console.error("Check INN error:", error);
      return {
        available: false,
        message: "Ошибка проверки ИНН",
      };
    }
  }

  /**
   * Проверка доступности email
   */
  async checkEmail(email: string): Promise<CheckEmailResponse> {
    try {
      const response: AxiosResponse<CheckEmailResponse> = await axios.get(
        `${API_BASE_URL}/v2/auth/check-email/${encodeURIComponent(email)}`,
      );
      return response.data;
    } catch (error) {
      console.error("Check email error:", error);
      return {
        available: false,
        message: "Ошибка проверки email",
      };
    }
  }

  /**
   * Выход из системы
   */
  logout(): void {
    this.stopAutoRefresh();
    this.token = null;
    this.user = null;
    this.tokenExpiresAt = null;
    this.clearAuthState();
  }

  /**
   * Проверка аутентификации.
   *
   * Протухший токен приравнивается к отсутствию входа: раньше UI считал
   * пользователя авторизованным до первого 401 и падал на середине действия.
   */
  isAuthenticated(): boolean {
    return !!this.token && !!this.user && !this.isTokenExpired();
  }

  /** Истёк ли срок действия токена по локальным часам. */
  isTokenExpired(): boolean {
    return this.tokenExpiresAt !== null && Date.now() >= this.tokenExpiresAt;
  }

  /** Момент истечения токена (ms epoch) либо null, если срок неизвестен. */
  getTokenExpiresAt(): number | null {
    return this.tokenExpiresAt;
  }

  /**
   * Продлевает сессию через POST /v2/auth/refresh.
   *
   * Параллельные вызовы (несколько 401 подряд + таймер) разделяют один
   * сетевой запрос, иначе бэкенд получит шквал одинаковых продлений.
   */
  async refresh(): Promise<boolean> {
    if (!this.token || !this.user) {
      return false;
    }
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    const request = this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    this.refreshInFlight = request;
    return request;
  }

  /**
   * Продлевает сессию, если до истечения осталось меньше REFRESH_LEAD_MS.
   *
   * Нужна для возврата фокуса на вкладку: в фоне браузер душит таймеры,
   * и запланированное продление могло не сработать вовремя.
   */
  async refreshIfExpiringSoon(): Promise<boolean> {
    if (!this.token || !this.user || this.tokenExpiresAt === null) {
      return false;
    }
    if (this.tokenExpiresAt - Date.now() > REFRESH_LEAD_MS) {
      return false;
    }
    return this.refresh();
  }

  /**
   * Включает фоновое продление сессии: таймер + проверка при возврате фокуса.
   *
   * Для неавторизованного пользователя ничего не запускаем.
   */
  startAutoRefresh(): void {
    if (!this.token || !this.user) {
      return;
    }
    if (!this.focusHandler) {
      this.focusHandler = () => {
        if (document.visibilityState === "visible") {
          void this.refreshIfExpiringSoon();
          // Таймер в фоне мог сместиться, пересобираем расписание по реальному сроку
          this.scheduleRefresh();
        }
      };
      document.addEventListener("visibilitychange", this.focusHandler);
      window.addEventListener("focus", this.focusHandler);
    }
    this.scheduleRefresh();
  }

  /** Останавливает фоновое продление и снимает обработчики. */
  stopAutoRefresh(): void {
    this.clearRefreshTimer();
    if (this.focusHandler) {
      document.removeEventListener("visibilitychange", this.focusHandler);
      window.removeEventListener("focus", this.focusHandler);
      this.focusHandler = null;
    }
  }

  /**
   * Регистрирует реакцию приложения на окончательно потерянную сессию,
   * чтобы сервис не зависел от React-слоя и не дёргал роутер напрямую.
   */
  setSessionExpiredHandler(handler: (() => void) | null): void {
    this.sessionExpiredHandler = handler;
  }

  /** Сессия потеряна безвозвратно: чистим состояние и уведомляем приложение. */
  handleSessionExpired(): void {
    const handler = this.sessionExpiredHandler;
    this.logout();
    handler?.();
  }

  private async performRefresh(): Promise<boolean> {
    try {
      const response: AxiosResponse<RefreshTokenResponse> = await axios.post(
        `${API_BASE_URL}/v2/auth/refresh`,
        undefined,
        { headers: { ...this.getAuthHeaders() } },
      );
      const { success, access_token: accessToken, expires_in: expiresIn } = response.data;
      if (!success || !accessToken) {
        return false;
      }
      this.applyToken(accessToken, expiresIn);
      this.saveAuthState();
      this.scheduleRefresh();
      return true;
    } catch (error) {
      console.error("Не удалось продлить сессию:", error);
      return false;
    }
  }

  /**
   * Запоминает токен и вычисляет срок его жизни.
   *
   * `exp` из самого токена приоритетнее `expires_in`: он не зависит от того,
   * сколько ответ шёл по сети.
   */
  private applyToken(token: string, expiresInSeconds?: number): void {
    this.token = token;
    const expiryFromToken = readTokenExpiry(token);
    if (expiryFromToken !== null) {
      this.tokenExpiresAt = expiryFromToken;
      return;
    }
    this.tokenExpiresAt =
      typeof expiresInSeconds === "number" && Number.isFinite(expiresInSeconds)
        ? Date.now() + expiresInSeconds * 1000
        : null;
  }

  private scheduleRefresh(): void {
    this.clearRefreshTimer();
    if (!this.token || !this.user || this.tokenExpiresAt === null) {
      return;
    }
    const delay = Math.max(this.tokenExpiresAt - Date.now() - REFRESH_LEAD_MS, 0);
    this.refreshTimer = setTimeout(() => {
      void this.runScheduledRefresh();
    }, delay);
  }

  private async runScheduledRefresh(): Promise<void> {
    if (await this.refresh()) {
      // Успешный refresh уже перепланировал таймер на новый срок
      return;
    }
    if (this.isTokenExpired()) {
      this.handleSessionExpired();
      return;
    }
    // Токен ещё жив — вероятно, моргнула сеть, пробуем ещё раз
    this.clearRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      void this.runScheduledRefresh();
    }, REFRESH_RETRY_MS);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Получение токена
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Получение данных пользователя
   */
  getUser(): AuthUser | null {
    return this.user;
  }

  /**
   * Проверка роли пользователя
   */
  hasRole(role: "ADMIN" | "MANAGER" | "ACCOUNTANT" | "CLIENT"): boolean {
    return this.user?.role === role;
  }

  /**
   * Проверка прав доступа (ADMIN имеет все права)
   */
  hasPermission(requiredRole: "ADMIN" | "MANAGER" | "ACCOUNTANT" | "CLIENT"): boolean {
    if (!this.user) return false;

    // ADMIN имеет все права
    if (this.user.role === "ADMIN") return true;

    // Иерархия ролей: ADMIN > MANAGER > ACCOUNTANT > CLIENT
    const roleHierarchy = ["CLIENT", "ACCOUNTANT", "MANAGER", "ADMIN"];
    const userRoleLevel = roleHierarchy.indexOf(this.user.role);
    const requiredRoleLevel = roleHierarchy.indexOf(requiredRole);

    return userRoleLevel >= requiredRoleLevel;
  }

  /**
   * Сохранение состояния аутентификации в localStorage
   */
  private saveAuthState(): void {
    if (this.token && this.user) {
      localStorage.setItem("auth_token", this.token);
      localStorage.setItem("auth_user", JSON.stringify(this.user));
    }
  }

  /**
   * Восстановление состояния аутентификации из localStorage
   */
  private restoreAuthState(): void {
    const token = localStorage.getItem("auth_token");
    const userStr = localStorage.getItem("auth_user");

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as AuthUser;
        // Протухший токен намеренно не выбрасываем: последнее слово за сервером
        // (часы клиента могут спешить), приложение попробует продлить сессию
        this.applyToken(token);
        this.user = user;
      } catch (error) {
        console.error("Failed to restore auth state:", error);
        this.clearAuthState();
      }
    }
  }

  /**
   * Очистка состояния аутентификации из localStorage
   */
  private clearAuthState(): void {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    // Ключи прежней схемы хранения — иначе после выхода их подхватят
    // потребители, читающие access_token напрямую
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }

  /**
   * Получение заголовков авторизации для API запросов
   */
  getAuthHeaders(): Record<string, string> {
    if (this.token) {
      return {
        Authorization: `Bearer ${this.token}`,
      };
    }
    return {};
  }
}

// Экспортируем singleton экземпляр
export const authService = new AuthService();
export default authService;
